'use client';

import { createContext, ReactNode, useContext, useEffect } from "react";
import { PublicationStatus, ResearchBlogEntry } from "@/app/lib/generated/graphql-types";
import {
    useCollectionTypeEditor,
    UseCollectionTypeEditorType,
} from "@/app/lib/hooks/use-editor/useCollectionTypeEditor";
import type { ResearchOverviewItemEditable } from "@/app/features/sites/research/types/types";
import { ResearchOverviewItemSchema } from "@/app/features/sites/research/types/schema";
import { getResearchBlogEntry } from "@/app/features/sites/research/data-access/queries";
import {
    researchOverviewItemInputFrom,
    updateResearchOverviewItem,
} from "@/app/features/sites/research/data-access/mutations";
import { useSiteResearchEditor } from "@/app/features/sites/research/contexts/SiteResearchEditorContext";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";

const ResearchOverviewItemEditorContext = createContext<UseCollectionTypeEditorType<ResearchOverviewItemEditable> | undefined>(undefined);

export function useResearchOverviewItemEditor() {
    const context = useContext(ResearchOverviewItemEditorContext);
    if (!context) {
        throw new Error("useResearchOverviewItemEditor must be used within ResearchOverviewItemEditorProvider");
    }
    return context;
}

function editableFromResearchBlogEntry(entry: ResearchBlogEntry): ResearchOverviewItemEditable {
    return {
        id: entry.documentId,
        title: entry.title,
        subtitle: entry.subtitle,
        excerpt: entry.excerpt,
        image: entry.image,
    };
}

export default function ResearchOverviewItemEditorProvider({
    documentId,
    children,
}: {
    documentId: string;
    children: ReactNode;
}) {
    const { registerChildEditor, unregisterChildEditor } = useSiteResearchEditor();
    const editor = useCollectionTypeEditor<
        ResearchBlogEntry,
        ResearchOverviewItemEditable,
        Awaited<ReturnType<typeof researchOverviewItemInputFrom>>
    >({
        documentId,
        schema: ResearchOverviewItemSchema,
        loadEntity: getResearchBlogEntry,
        update: updateResearchOverviewItem,
        toEditable: editableFromResearchBlogEntry,
        toInput: researchOverviewItemInputFrom,
        autosave: async (targetDocumentId, data) => updateResearchOverviewItem({
            documentId: targetDocumentId,
            data,
            status: PublicationStatus.Draft,
        }),
        manageDraftMode: false,
    });

    useEffect(() => {
        registerChildEditor(documentId, {
            hasChanges: editor.hasChanges,
            autosaveState: editor.autosaveState,
        }, {
            publish: editor.publish,
            discardChanges: editor.discardChanges,
        });

        return () => unregisterChildEditor(documentId);
    }, [
        documentId,
        editor.autosaveState,
        editor.discardChanges,
        editor.hasChanges,
        editor.publish,
        registerChildEditor,
        unregisterChildEditor,
    ]);

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <ResearchOverviewItemEditorContext.Provider value={editor}>
            {children}
        </ResearchOverviewItemEditorContext.Provider>
    );
}
